import { daysOfWeek } from 'us-react-components/dist/constants/DaysOfWeek';
import { DateFormat, formatDateToString, parseDateInput } from 'us-react-components/dist/utils/date';
import { AiFamilyNux, RecurringDetails } from 'us-web-services';
import { FamilyRegistrationStore, LabeledId } from './FamilyRegistrationStore';

export const parseCareDate = (value: string): Date | undefined => {
  const parsed = parseDateInput(value);

  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export const buildAiFamilyNuxFromStore = (familyRegistration: FamilyRegistrationStore): AiFamilyNux => ({
  lookingForId: familyRegistration.lookingForId,
  justBrowsing: familyRegistration.justBrowsing,
  address: {
    city: familyRegistration.profile?.address?.city || undefined,
    neighborhood: familyRegistration.profile?.address?.neighborhood || undefined,
    state: familyRegistration.profile?.address?.state || undefined,
    zip: familyRegistration.profile?.address?.zip || undefined,
  },
  availabilityType: familyRegistration.availabilityType,
  jobType: familyRegistration.jobType,
  startDate: familyRegistration.startDate
    ? formatDateToString(familyRegistration.startDate, DateFormat.API_DATE_FORMAT)
    : undefined,
  endDate: familyRegistration.endDate
    ? formatDateToString(familyRegistration.endDate, DateFormat.API_DATE_FORMAT)
    : undefined,
  startTime: familyRegistration.startTime,
  endTime: familyRegistration.endTime,
  countDependents: familyRegistration.countDependents,
  childrenAges: familyRegistration.childrenAges.map(age => age.value).filter(v => v),
  details: familyRegistration.details,
  recurring: familyRegistration.recurring?.days?.length
    ? { days: familyRegistration.recurring.days.map(d => d.weekday?.code).filter(Boolean) as string[] }
    : undefined,
  dog: familyRegistration.dog,
  cat: familyRegistration.cat,
  otherPet: familyRegistration.otherPet,
  languages: familyRegistration.languages?.length ? familyRegistration.languages : undefined,
  teachingSkills: familyRegistration.teachingSkills?.length ? familyRegistration.teachingSkills : undefined,
  specialSituations: familyRegistration.specialSituations?.length ? familyRegistration.specialSituations : undefined,
  specialServices: familyRegistration.specialServices?.length ? familyRegistration.specialServices : undefined,
  specialExperiences: familyRegistration.specialExperiences?.length ? familyRegistration.specialExperiences : undefined,
});

const toLabeledIds = (items: LabeledId[]): LabeledId[] =>
  items.filter(item => item.id !== undefined).map(item => ({ id: item.id, label: item.label }));

export const syncStoreFromAiNux = (familyRegistration: FamilyRegistrationStore, nuxData: AiFamilyNux): void => {
  if (nuxData.lookingForId) {
    familyRegistration.setLookingFor(nuxData.lookingForId);
  }
  if (nuxData.justBrowsing !== undefined) {
    familyRegistration.setJustBrowsing(nuxData.justBrowsing);
  }
  if (nuxData.address?.zip) {
    familyRegistration.setZipCode(nuxData.address.zip);
    if (nuxData.address?.city) {
      familyRegistration.setCity(nuxData.address.city);
    }
    if (nuxData.address?.neighborhood) {
      familyRegistration.setNeighborhood(nuxData.address.neighborhood);
    }
    if (nuxData.address?.state) {
      familyRegistration.setState(nuxData.address.state);
    }
  }
  if (nuxData.availabilityType) {
    familyRegistration.setAvailabilityType(nuxData.availabilityType);
  }
  if (nuxData.jobType) {
    familyRegistration.setJobType(nuxData.jobType);
  }
  if (nuxData.startDate) {
    const startDate = parseCareDate(nuxData.startDate);
    if (startDate) {
      familyRegistration.setStartDate(startDate);
    }
  }
  if (nuxData.endDate) {
    const endDate = parseCareDate(nuxData.endDate);
    if (endDate) {
      familyRegistration.setEndDate(endDate);
    }
  }
  if (nuxData.startTime) {
    familyRegistration.setStartTime(nuxData.startTime);
  }
  if (nuxData.endTime) {
    familyRegistration.setEndTime(nuxData.endTime);
  }
  if (nuxData.includes && nuxData.includes.length > 0) {
    const validIncludes = nuxData.includes
      .filter(inc => inc.id !== undefined)
      .map(inc => ({ id: inc.id, label: inc.label }));
    if (validIncludes.length > 0) {
      familyRegistration.setIncludes(validIncludes);
    }
  }
  if (nuxData.sweeteners && nuxData.sweeteners.length > 0) {
    const validSweeteners = nuxData.sweeteners
      .filter(sw => sw.id !== undefined)
      .map(sw => ({ id: sw.id, label: sw.label }));
    if (validSweeteners.length > 0) {
      familyRegistration.setSweeteners(validSweeteners);
    }
  }
  if (nuxData.countDependents) {
    familyRegistration.setCountDependents(nuxData.countDependents);
  }
  if (nuxData.childrenAges && nuxData.childrenAges.length > 0) {
    familyRegistration.setChildrenAges(
      nuxData.childrenAges.map((age: string, index: number) => ({ id: index, value: age }))
    );
  }
  if (nuxData.details) {
    familyRegistration.setDetails(nuxData.details);
  }
  if (nuxData.recurring && typeof nuxData.recurring === 'object') {
    const recurringData = nuxData.recurring as { days?: string[] } | RecurringDetails;
    if ('days' in recurringData && Array.isArray(recurringData.days) && recurringData.days.length > 0 && typeof recurringData.days[0] === 'string') {
      const dayCodes = recurringData.days as string[];
      const recurringDetails: RecurringDetails = {
        days: dayCodes.map(code => {
          const dayOption = daysOfWeek.find(d => d.code === code);
          return dayOption ? { weekday: { ...dayOption } } : null;
        }).filter(Boolean) as RecurringDetails['days'],
        flexible: true,
      };
      familyRegistration.setRecurring(recurringDetails);
    } else if ((recurringData as RecurringDetails).days?.length) {
      familyRegistration.setRecurring(recurringData as RecurringDetails);
    }
  }
  if (nuxData.dog !== undefined) {
    familyRegistration.setDog(nuxData.dog);
  }
  if (nuxData.cat !== undefined) {
    familyRegistration.setCat(nuxData.cat);
  }
  if (nuxData.otherPet !== undefined) {
    familyRegistration.setOtherPet(nuxData.otherPet);
  }
  if (nuxData.languages && nuxData.languages.length > 0) {
    familyRegistration.setLanguages(toLabeledIds(nuxData.languages));
  }
  if (nuxData.teachingSkills && nuxData.teachingSkills.length > 0) {
    familyRegistration.setTeachingSkills(toLabeledIds(nuxData.teachingSkills));
  }
  if (nuxData.specialSituations && nuxData.specialSituations.length > 0) {
    familyRegistration.setSpecialSituations(toLabeledIds(nuxData.specialSituations));
  }
  if (nuxData.specialServices && nuxData.specialServices.length > 0) {
    familyRegistration.setSpecialServices(toLabeledIds(nuxData.specialServices));
  }
  if (nuxData.specialExperiences && nuxData.specialExperiences.length > 0) {
    familyRegistration.setSpecialExperiences(toLabeledIds(nuxData.specialExperiences));
  }
};
